import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { API, getAuthHeaders } from '../utils/api';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  const planId = searchParams.get('plan_id');
  
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    // Handle PayPal return
    if (paymentId && payerId) {
      executePayPalPayment();
      return;
    }
    
    // Handle PayPal return (only payment method)
    if (sessionId) {
      checkPaymentStatus();
      return;
    }
    
    navigate('/pricing');
  }, [sessionId, paymentId, payerId]);

  const executePayPalPayment = async () => {
    // Prevent duplicate execution
    if (status !== 'checking') return;
    
    try {
      const response = await axios.post(
        `${API}/payments/paypal/execute?payment_id=${paymentId}&payer_id=${payerId}`,
        {},
        { withCredentials: true, headers: getAuthHeaders() }
      );
      
      setStatus('success');
      setPlanInfo({ plan_id: planId || 'pro' });
    } catch (error) {
      console.error('PayPal execution failed:', error);
      
      // Check if the error is "already processed" - this is actually a success
      const errorMessage = error.response?.data?.detail || '';
      if (errorMessage.toLowerCase().includes('already') || 
          errorMessage.toLowerCase().includes('done')) {
        // Payment was already completed - show success
        setStatus('success');
        setPlanInfo({ plan_id: planId || 'pro' });
        return;
      }
      
      setStatus('failed');
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/payments/checkout/status/${sessionId}`,
        { withCredentials: true, headers: getAuthHeaders() }
      );

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPlanInfo(response.data);
      } else {
        // Keep polling
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Failed to check payment:', error);
      setStatus('failed');
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-[#121214] border-zinc-800 p-12 text-center max-w-md">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
          <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Processing Payment</h1>
          <p className="text-slate-400">Please wait while we confirm your payment...</p>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <Card className="bg-[#121214] border-zinc-800 p-12 text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold font-['Manrope'] mb-2">Payment Failed</h1>
          <p className="text-slate-400 mb-6">We couldn't verify your payment. Please try again or contact <a href="mailto:supporteam@gettradeforgeapp.com" className="text-emerald-400 hover:underline">supporteam@gettradeforgeapp.com</a>.</p>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/pricing')} className="flex-1 bg-blue-600 hover:bg-blue-700">
              Try Again
            </Button>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="flex-1">
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4" data-testid="payment-success-page">
      <Card className="bg-[#121214] border-zinc-800 p-12 text-center max-w-md">
        <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-12 h-12 text-emerald-500" />
        </div>
        
        <h1 className="text-3xl font-bold font-['Manrope'] mb-3">Payment Successful!</h1>
        <p className="text-lg text-slate-400 mb-2">Welcome to Pro! 🎉</p>
        <p className="text-sm text-slate-500 mb-8">
          Your subscription has been activated and you now have access to all premium features.
        </p>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-8 text-left">
          <p className="text-sm text-slate-400 mb-1">Plan</p>
          <p className="text-lg font-semibold capitalize">
            {planInfo?.plan_id?.replace('_', ' ')}
          </p>
        </div>

        <div className="space-y-3">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="go-to-dashboard-btn"
          >
            Go to Dashboard
          </Button>
          <Button 
            onClick={() => navigate('/signals')} 
            variant="outline"
            className="w-full border-zinc-700 hover:bg-zinc-800"
          >
            View Signals
          </Button>
        </div>
      </Card>
    </div>
  );
}
